import { useState, useEffect } from 'react';
import { FaTrash, FaImage, FaSpinner, FaPlus, FaVolumeUp, FaLanguage } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const createEmptyWordSplit = () => ({
  question: '',
  leftWord: '',
  rightWord: '',
  questionMeaning: '',
  answerMeaning: '',
  confusingLetters: '',
  languageAudio: null,
  englishAudio: null,
  image: null
});

const WordSplitActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [createEmptyWordSplit()]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  const buildStoragePath = ({ wordId, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'word_split',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent({
          words: Array.isArray(parsed?.words) && parsed.words.length > 0
            ? parsed.words.map(word => ({ ...createEmptyWordSplit(), ...word }))
            : [createEmptyWordSplit()]
        });
      } catch {
        setContent({ words: [createEmptyWordSplit()] });
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addWord = () => {
    setContent(prev => ({
      ...prev,
      words: [...prev.words, createEmptyWordSplit()]
    }));
  };

  const removeWord = async (index) => {
    if (content.words.length <= 1) return;

    const wordToDelete = content.words[index];
    const storage = getStorage();
    const fileTypes = [
      'languageAudio',
      'englishAudio',
      'image'
    ];

    for (const fileType of fileTypes) {
      if (wordToDelete[fileType]) {
        try {
          const fileRef = ref(storage, wordToDelete[fileType]);
          await deleteObject(fileRef);
        } catch {
          // Ignore cleanup failures for already-missing files
        }
      }
    }

    setContent(prev => ({
      ...prev,
      words: prev.words.filter((_, i) => i !== index)
    }));
  };

  const updateWord = (index, field, value) => {
    const updatedWords = [...content.words];
    updatedWords[index] = { ...updatedWords[index], [field]: value };
    setContent(prev => ({ ...prev, words: updatedWords }));
  };

  const handleFileUpload = async (index, fileType, file) => {
    if (!file) return;

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const wordId = `word${index + 1}`;
      const storagePath = buildStoragePath({ wordId, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      await getDownloadURL(storageRef);

      const oldPath = content.words[index][fileType];
      if (oldPath) {
        try {
          const oldRef = ref(storage, oldPath);
          await deleteObject(oldRef);
        } catch {
          // Ignore cleanup failures for replaced files
        }
      }

      updateWord(index, fileType, storagePath);
    } catch (error) {
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) {
      alert('No audio file available');
      return;
    }

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play();
    } catch {
      alert('Failed to play audio');
    }
  };

  const translateAllEnglish = async () => {
    setTranslating(true);

    try {
      const storage = getStorage();
      const translationTargets = [];

      content.words.forEach((word, index) => {
        if (word.question?.trim() && !word.questionMeaning?.trim()) {
          translationTargets.push({ index, field: 'questionMeaning', sourceText: word.question });
        }
        const answerText = [word.question, word.leftWord, word.rightWord].filter(Boolean).join('      ');
        if (answerText.trim() && !word.answerMeaning?.trim()) {
          translationTargets.push({ index, field: 'answerMeaning', sourceText: answerText });
        }
      });

      if (translationTargets.length === 0) {
        alert('All word split items already have English meanings or there is no language text to translate.');
        return;
      }

      const translatedTexts = await translateBatchToText(
        translationTargets.map(item => item.sourceText),
        db,
        5,
        2000,
        Constants.LANGUAGES.KANNADA,
        Constants.LANGUAGES.ENGLISH
      );

      const updatedWords = [...content.words];
      const englishAudioRequests = [];

      translationTargets.forEach((target, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedWords[target.index][target.field] = translatedText;
        }
      });

      updatedWords.forEach((word, index) => {
        const combinedMeaning = [word.questionMeaning, word.answerMeaning].filter(Boolean).join(' ');
        if (combinedMeaning.trim()) {
          englishAudioRequests.push({
            text: combinedMeaning,
            index,
            audioField: 'englishAudio'
          });
        }
      });

      if (englishAudioRequests.length > 0) {
        const englishAudioUrls = await generateBatchSpeechAudio(
          englishAudioRequests.map(request => ({
            text: request.text,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE
          })),
          db,
          storage
        );

        englishAudioRequests.forEach((request, requestIndex) => {
          const audioUrl = englishAudioUrls[requestIndex];
          if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
            updatedWords[request.index][request.audioField] = audioUrl;
          }
        });
      }

      setContent({ ...content, words: updatedWords });
      alert('Translation and audio generation completed successfully!');
    } catch (error) {
      alert('Failed to translate words: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  const generateAllAudio = async () => {
    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const requests = [];

      content.words.forEach((word, index) => {
        const languageText = [word.question, word.leftWord, word.rightWord].filter(Boolean).join(' ');
        const englishText = [word.questionMeaning, word.answerMeaning].filter(Boolean).join(' ');

        if (languageText.trim() && !word.languageAudio) {
          requests.push({ index, field: 'languageAudio', text: languageText, languageCode: LanguageCodes.KANNADA });
        }
        if (englishText.trim() && !word.englishAudio) {
          requests.push({ index, field: 'englishAudio', text: englishText, languageCode: LanguageCodes.ENGLISH });
        }
      });

      if (requests.length === 0) {
        alert('No text found to generate audio.');
        return;
      }

      const audioUrls = await generateBatchSpeechAudio(
        requests.map(request => ({
          text: request.text,
          languageCode: request.languageCode,
          ssmlGender: VoiceGenders.FEMALE
        })),
        db,
        storage
      );

      const updatedWords = [...content.words];
      let audioGenerated = false;

      requests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          updatedWords[request.index][request.field] = audioUrl;
          audioGenerated = true;
        }
      });

      setContent({ ...content, words: updatedWords });

      if (audioGenerated) {
        alert('Audio generated successfully!');
      } else {
        alert('Failed to generate audio.');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Word Split</h3>
            <p className="text-sm text-gray-600 mt-1">{content.words.length} word split{content.words.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={translateAllEnglish}
              disabled={translating || generatingAudio}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {translating ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <FaLanguage className="w-4 h-4 mr-2" />
                  Translate Meanings
                </>
              )}
            </button>
            <button
              type="button"
              onClick={generateAllAudio}
              disabled={generatingAudio || translating}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm disabled:bg-emerald-400 disabled:cursor-not-allowed"
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              onClick={addWord}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Word Split
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {content.words.map((word, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
                    placeholder="Question *"
                    value={word.question || ''}
                    onChange={(e) => updateWord(index, 'question', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
                    placeholder="Left word *"
                    value={word.leftWord || ''}
                    onChange={(e) => updateWord(index, 'leftWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
                    placeholder="Right word *"
                    value={word.rightWord || ''}
                    onChange={(e) => updateWord(index, 'rightWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
                    placeholder="Question meaning *"
                    value={word.questionMeaning || ''}
                    onChange={(e) => updateWord(index, 'questionMeaning', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
                    placeholder="Answer meaning *"
                    value={word.answerMeaning || ''}
                    onChange={(e) => updateWord(index, 'answerMeaning', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm md:col-span-2"
                    placeholder="Confusing letters (space-separated) *"
                    value={word.confusingLetters || ''}
                    onChange={(e) => updateWord(index, 'confusingLetters', e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap max-w-48">
                  {[
                    ['languageAudio', 'LA'],
                    ['englishAudio', 'EA']
                  ].map(([field, label]) => (
                    <div key={field} className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`${field}-${index}`}
                        onChange={(e) => handleFileUpload(index, field, e.target.files[0])}
                        disabled={uploadingFiles[`${index}-${field}`]}
                      />
                      <label
                        htmlFor={`${field}-${index}`}
                        className="cursor-pointer px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs hover:bg-teal-100 transition-colors"
                        title={label}
                      >
                        🎵 {label}
                      </label>
                      {uploadingFiles[`${index}-${field}`] && <FaSpinner className="w-3 h-3 animate-spin text-teal-600" />}
                      {word[field] && !uploadingFiles[`${index}-${field}`] && (
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word[field])}
                          title={`Play ${label}`}
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}

                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`image-${index}`}
                      onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-image`]}
                    />
                    <label
                      htmlFor={`image-${index}`}
                      className="cursor-pointer px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs hover:bg-pink-100 transition-colors"
                      title="Upload Image"
                    >
                      🖼️ IMG
                    </label>
                    {uploadingFiles[`${index}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-pink-600" />}
                    {word.image && !uploadingFiles[`${index}-image`] && (
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={async () => {
                          try {
                            const storage = getStorage();
                            const imageRef = ref(storage, word.image);
                            const downloadURL = await getDownloadURL(imageRef);
                            window.open(downloadURL, '_blank');
                          } catch {
                            alert('Failed to open image');
                          }
                        }}
                        title="View Image"
                      >
                        <FaImage className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {content.words.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                    onClick={() => removeWord(index)}
                    title="Remove Word Split"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const validateWordSplitContent = (content) => {
  const errors = [];

  if (!content || typeof content !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }

  if (!content.words || !Array.isArray(content.words)) {
    errors.push('Content must have a words array');
    return errors;
  }

  if (content.words.length === 0) {
    errors.push('At least one word split is required');
    return errors;
  }

  content.words.forEach((word, index) => {
    if (!word.question?.trim()) {
      errors.push(`Word split ${index + 1}: question is required`);
    }
    if (!word.leftWord?.trim()) {
      errors.push(`Word split ${index + 1}: left word is required`);
    }
    if (!word.rightWord?.trim()) {
      errors.push(`Word split ${index + 1}: right word is required`);
    }
    if (!word.questionMeaning?.trim()) {
      errors.push(`Word split ${index + 1}: question meaning is required`);
    }
    if (!word.answerMeaning?.trim()) {
      errors.push(`Word split ${index + 1}: answer meaning is required`);
    }
    if (!word.confusingLetters?.trim()) {
      errors.push(`Word split ${index + 1}: confusing letters are required`);
    }

    if (word.questionMeaning?.trim() && !/^[A-Z]/.test(word.questionMeaning.trim())) {
      errors.push(`Word split ${index + 1}: question meaning must start with a capital letter`);
    }
    if (word.answerMeaning?.trim() && !/^[A-Z]/.test(word.answerMeaning.trim())) {
      errors.push(`Word split ${index + 1}: answer meaning must start with a capital letter`);
    }
  });

  return errors;
};

export default WordSplitActivity;
